import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPackage, FiDroplet, FiWind, FiRefreshCw, FiArrowRight } = FiIcons;

const steps = [
  {
    icon: FiPackage,
    title: "Vessel Arrival",
    desc: "Your premium glass vessels arrive once. High-durability, lead-free, and designed to look beautiful on any counter."
  },
  {
    icon: FiDroplet,
    title: "The Concentrate",
    desc: "Drop a water-soluble refill pod into your vessel. No measuring, no mess, just pure active ingredients."
  },
  {
    icon: FiWind,
    title: "Pure Clean",
    desc: "Fill with warm tap water and watch the magic happen. Effective, plant-based cleaning in minutes."
  },
  {
    icon: FiRefreshCw,
    title: "Automated Loop",
    desc: "Set your refill schedule in your workspace. We ship pods in slim paper mailers right when you're running low."
  }
];

export default function RefillLoop() {
  return (
    <section id="how-it-works" className="py-24 md:py-32 bg-[var(--navy)] text-[var(--kraft-light)] relative overflow-hidden">
      <div className="absolute -top-24 -left-24 w-96 h-96 border border-[var(--navy-light)] rounded-full opacity-20" />
      <div className="absolute -bottom-48 -right-48 w-[600px] h-[600px] border-2 border-[var(--navy-light)] rounded-full opacity-10" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-20 items-center mb-20">
          <div className="max-w-3xl">
            <h2 className="text-4xl md:text-6xl font-bold mb-8 text-[var(--mint)] leading-tight italic">
              The Circular <br/>Refill Experience
            </h2>
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed">
              We've redesigned the supply chain to eliminate the heavy water weight and plastic waste. You get the power, we handle the circularity.
            </p>
          </div>
          
          {/* In-context process image: Refill Pod detail */}
          <div className="relative aspect-[16/9] rounded-2xl overflow-hidden shadow-2xl group">
            <img 
              src="https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&q=80&w=1000" 
              alt="Close-up of a water-soluble refill pod being placed into an amber glass bottle"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              width="600"
              height="338"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)]/60 to-transparent opacity-60" />
            <div className="absolute bottom-4 left-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[var(--mint)]" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-white">Active Concentrate Detail</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {steps.map((step, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="relative group"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-[var(--navy-light)] flex items-center justify-center group-hover:bg-[var(--mint)] transition-all duration-500 shadow-lg group-hover:-rotate-6">
                  <SafeIcon icon={step.icon} className="text-2xl text-[var(--mint)] group-hover:text-[var(--navy)] transition-colors" />
                </div>
                <div className="h-[1px] flex-grow bg-[var(--navy-light)] hidden lg:block" />
              </div>
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <span className="text-[var(--mint)] font-serif italic text-2xl opacity-50">0{index + 1}</span>
                {step.title}
              </h3>
              <p className="text-sm text-gray-400 leading-relaxed group-hover:text-gray-200 transition-colors">
                {step.desc}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="mt-20 pt-12 border-t border-[var(--navy-light)] flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">90%</div>
              <div className="text-[10px] uppercase tracking-widest text-[var(--mint)]">Lighter Shipping</div>
            </div>
            <div className="w-[1px] h-12 bg-[var(--navy-light)]" />
            <div className="text-center">
              <div className="text-3xl font-bold text-white">0%</div>
              <div className="text-[10px] uppercase tracking-widest text-[var(--mint)]">Plastic Waste</div>
            </div>
          </div>
          <button className="flex items-center gap-3 text-[var(--mint)] font-bold hover:gap-5 transition-all group">
            Start your first kit <SafeIcon icon={FiArrowRight} className="text-xl" />
          </button>
        </div>
      </div>
    </section>
  );
}